import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import { entries } from '@debbie/common/dist/util/index.js';
import THEME from '../../theme.js';
import { ChevronDown, ChevronUp, Filter } from '../Icons/index.js';
import Input from '../Input.js';
import PlaceholderLoader from '../PlaceholderLoader.js';
const PowerView = (props) => {
    const lang = useSelector((s) => s.lang);
    const [selected, setSelected] = useState(new Set());
    const [openFilter, setOpenFilter] = useState(null);
    const [filterLoc, setFilterLoc] = useState({ x: 0, y: 0 });
    const [filterDirection, setFilterDirection] = useState('left');
    const values = [];
    for (const key in props.values) {
        if (props.values[key] != undefined) {
            const entry = [key, props.values[key]];
            values.push(entry);
        }
    }
    const showing = props.data;
    const allSelected = useMemo(() => {
        if (props.selectable) {
            for (const el of showing) {
                if (!selected.has(props.selectKey(el))) {
                    return false;
                }
            }
            return true;
        }
        return false;
    }, [showing, selected]);
    const addSelected = (id) => {
        if (props.selectable) {
            const newSet = new Set([...selected.values(), id]);
            setSelected(newSet);
            props.onSelect([...newSet.values()]);
        }
    };
    const removeSelected = (id) => {
        if (props.selectable) {
            const newSet = new Set([...selected.values()]);
            newSet.delete(id);
            setSelected(newSet);
            props.onSelect([...newSet.values()]);
        }
    };
    const selectVisible = () => {
        if (props.selectable) {
            const newSet = new Set([...selected.values()]);
            for (const el of showing) {
                newSet.add(props.selectKey(el));
            }
            setSelected(newSet);
            props.onSelect([...newSet.values()]);
        }
    };
    const deselectVisible = () => {
        if (props.selectable) {
            const newSet = new Set([...selected.values()]);
            for (const el of showing) {
                newSet.delete(props.selectKey(el));
            }
            setSelected(newSet);
            props.onSelect([...newSet.values()]);
        }
    };
    const root = useRef(null);
    const filterRef = useRef(null);
    useEffect(() => {
        const handler = (e) => {
            if (filterRef.current &&
                e.target instanceof Node &&
                !filterRef.current.contains(e.target)) {
                setOpenFilter(null);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => {
            document.removeEventListener('mousedown', handler);
        };
    }, [filterRef]);
    return (React.createElement(FilterWrapper, null,
        React.createElement(Wrapper, { ref: root },
            React.createElement(Filters, { ref: filterRef, direction: filterDirection, visible: openFilter != null, ...filterLoc }, entries(props.values).map(([key, value]) => {
                if (key !== openFilter || value == null) {
                    return null;
                }
                if (value.filter) {
                    return React.createElement(value.filter, {
                        filter: props.filter,
                        setFilter: props.setFilter,
                        key: key,
                    });
                }
                return null;
            })),
            React.createElement("table", { style: { width: '100%' }, cellSpacing: "0", cellPadding: "0" },
                React.createElement(THead, null,
                    React.createElement(Tr, null,
                        props.selectable ? (React.createElement(Th, { selected: false, sortable: false },
                            React.createElement("input", { type: "checkbox", checked: allSelected, onChange: (e) => {
                                    if (e.currentTarget.checked) {
                                        selectVisible();
                                    }
                                    else {
                                        deselectVisible();
                                    }
                                } }))) : null,
                        values.map(([key, val], i) => {
                            const sortable = val.sortable == false ? false : true;
                            const sortKey = val.sortValue ?? key;
                            return (React.createElement(Th, { style: {
                                    width: val.width ? val.width : 'initial',
                                }, key: `${String(key)}${i}`, alignment: val.align, selected: sortKey === props.sortKey, sortable: sortable, onClick: () => {
                                    if (sortable) {
                                        if (props.sortKey === sortKey) {
                                            if (props.direction === 'ASC') {
                                                props.updateSortDirection('DESC');
                                            }
                                            else {
                                                props.updateSortKey(undefined);
                                            }
                                        }
                                        else {
                                            props.updateSortKey(sortKey);
                                            props.updateSortDirection('ASC');
                                        }
                                    }
                                } },
                                React.createElement(HeaderCellContent, null,
                                    React.createElement("div", { style: { display: 'flex', alignItems: 'center' } },
                                        React.createElement("span", { style: { whiteSpace: 'nowrap' } }, val.label),
                                        React.createElement(SortIcon, { sorting: sortKey === props.sortKey }, sortKey === props.sortKey ? (props.direction === 'ASC' ? (React.createElement(ChevronUp, null)) : (React.createElement(ChevronDown, null))) : (''))),
                                    val.filter ? (React.createElement(FilterIcon, { active: val.filterActive
                                            ? val.filterActive(props.filter)
                                            : false, onClick: (e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            if (openFilter === key) {
                                                setOpenFilter(null);
                                                return;
                                            }
                                            setOpenFilter(key);
                                            const bodyRect = root.current.getBoundingClientRect();
                                            const rect = e.currentTarget.offsetParent.getBoundingClientRect();
                                            setFilterDirection(i === 0 ? 'right' : 'left');
                                            setFilterLoc({
                                                x: Math.floor(rect.right - bodyRect.left),
                                                y: Math.floor(rect.bottom - rect.top),
                                            });
                                        } },
                                        React.createElement(Filter, null))) : null)));
                        }))),
                React.createElement(TBody, null,
                    props.searchable && (React.createElement(Tr, null,
                        React.createElement(Td, { style: {
                                backgroundColor: '#f3f3f3',
                                borderBottom: '1px solid lightgray',
                            }, colSpan: values.length + (props.selectable ? 1 : 0) },
                            React.createElement(Input, { placeholder: lang.table.search, autofocus: props.searchFocused, value: props.searchQuery, onChange: (e) => {
                                    if (props.setSearchQuery) {
                                        props.setSearchQuery(e.target.value);
                                    }
                                }, style: { marginBottom: '0' } })))),
                    !props.dataLoaded && props.loading && (React.createElement(Tr, null,
                        React.createElement(Td, { style: {
                                backgroundColor: '#f3f3f3',
                                borderBottom: '1px solid lightgray',
                            }, colSpan: values.length + (props.selectable ? 1 : 0) },
                            React.createElement(PlaceholderLoader, null)))),
                    props.dataLoaded && props.loading ? (React.createElement(Tr, null,
                        React.createElement(Td, { style: {
                                borderBottom: 'none',
                                padding: '0',
                            }, colSpan: values.length + (props.selectable ? 1 : 0) },
                            React.createElement(PlaceholderLoader, { style: { display: 'block' }, height: "2px" })))) : (React.createElement(Tr, null,
                        React.createElement(Td, { style: {
                                borderBottom: 'none',
                                padding: '0',
                            }, colSpan: values.length + (props.selectable ? 1 : 0) },
                            React.createElement("div", { style: { height: '2px' } })))),
                    showing.length > 0 ? (showing.map((d) => {
                        return (React.createElement(Tr, { key: props.genKey(d), clickable: typeof props.onClick === 'function' },
                            props.selectable ? (React.createElement(Td, null,
                                React.createElement("input", { type: "checkbox", checked: selected.has(props.selectKey(d)), onChange: (e) => {
                                        if (e.currentTarget.checked) {
                                            addSelected(props.selectKey(d));
                                        }
                                        else {
                                            removeSelected(props.selectKey(d));
                                        }
                                    } }))) : null,
                            values.map(([key, val], i) => {
                                return (React.createElement(Td, { key: `${String(key)}${i}`, alignment: val.align, onClick: () => {
                                        if (props.onClick) {
                                            props.onClick(d);
                                        }
                                    } }, val.map ? val.map(d[key], d) : d[key]));
                            })));
                    })) : props.dataLoaded ? (React.createElement(Tr, null,
                        React.createElement(Td, { style: {
                                backgroundColor: '#f3f3f3',
                                borderBottom: '1px solid lightgray',
                                textAlign: 'center',
                            }, colSpan: values.length + (props.selectable ? 1 : 0) }, props.emptyComponent && props.empty
                            ? React.createElement(props.emptyComponent, {})
                            : lang.table.noData))) : null)))));
};
const FilterWrapper = styled.div `
  position: relative;
`;
const Wrapper = styled.div `
  overflow: auto;
  width: 100%;
`;
const THead = styled.thead `
  tr {
    border-bottom: 1px solid lightgray;
  }
`;
const TBody = styled.tbody `
  tr:hover {
    background-color: #f3f3f3;
  }
`;
const Tr = styled.tr `
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'initial')};

  :hover {
  }
`;
const Th = styled.th `
  font-weight: normal;
  padding: 1em;
  border-bottom: 1px solid lightgray;
  cursor: ${({ sortable }) => (sortable ? 'pointer' : 'initial')};
  text-align: ${(props) => props.alignment ?? 'left'};
  font-weight: 900;

  color: ${THEME.COLOR_ACCENT};

  :hover {
    background: ${({ sortable }) => (sortable ? '#f3f3f3' : 'none')};
  }
`;
const HeaderCellContent = styled.div `
  display: flex;
  justify-content: space-between;
`;
const Td = styled.td `
  font-weight: normal;
  padding: 1em;
  text-align: ${(props) => props.alignment ?? 'left'};
  border-bottom: 1px solid #f0f0f0;
`;
const SortIcon = styled.span `
  height: 1em;
  width: ${(props) => (props.sorting ? '1em' : '0px')};
  transform: translateY(0.2em);
  display: inline-flex;
  align-items: center;
`;
const FilterIcon = styled.span `
  height: 1em;
  width: 1em;
  transform: translateY(0.2em);
  display: inline-flex;
  align-items: center;

  & polygon {
    fill: ${(p) => (p.active ? THEME.COLOR_ACCENT : 'none')};
  }
`;
const Filters = styled.div `
  display: ${(p) => (p.visible ? 'initial' : 'none')};
  background: #fff;
  box-shadow: 0px 1px 10px 1px rgba(0, 0, 0, 0.1);
  padding: 0.5em;
  position: absolute;
  top: ${(p) => p.y}px;
  left: ${(p) => p.x}px;
  width: 200px;
  z-index: 10;
  transform: ${(props) => props.direction === 'right'
    ? 'translateX(calc(0% - 36px))'
    : 'translateX(-100%)'};
`;
export default PowerView;
//# sourceMappingURL=View.js.map